import type { BodyInit } from 'undici-types';

import { useCampaignStore } from '@/entities/campaign/model';
import { AUTH_COOKIE_NAME } from '@/shared/api/constants/authCookieName';
import type { IApiFetchConfig, IRequestConfig, IResponseErrors } from '@/shared/api/types';
import { retrieveLaunchParams } from '@telegram-apps/sdk-react';
import Cookies from 'js-cookie';
import JSONbigFactory from 'json-bigint';
import { toast } from '../ui/Toast/ui/ToastContainer';
import { API_EVENT_EMITTER } from './ApiEventEmitter';
import { ERROR_MESSAGES } from './constants';

const JSONbigFast = JSONbigFactory({ alwaysParseAsBig: false, useNativeBigInt: true });
const JSONbigSafe = JSONbigFactory({ storeAsString: true });

const SAFE_MIN = BigInt(Number.MIN_SAFE_INTEGER);
const SAFE_MAX = BigInt(Number.MAX_SAFE_INTEGER);

function isIntegerString(s: string) {
	return /^-?\d+$/.test(s);
}

function isFloatString(s: string) {
	// eslint-disable-next-line no-useless-escape
	return /[.\eE]/.test(s) && /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(s);
}

function convertValue(value: any): any {
	if (typeof value === 'bigint') {
		// привести в number если безопасно
		if (value >= SAFE_MIN && value <= SAFE_MAX) return Number(value);

		return value;
	}

	if (typeof value === 'string') {
		// если строка выглядит как целое число
		if (isIntegerString(value)) {
			try {
				const bi = BigInt(value);

				if (bi >= SAFE_MIN && bi <= SAFE_MAX) return Number(bi);

				return bi;
			} catch {
				// если не преобразуется в BigInt (очень редко) — вернуть строку
				return value;
			}
		}

		// если строка похожа на дробь/экспоненту -> number
		if (isFloatString(value)) {
			const n = Number(value);

			return Number.isFinite(n) ? n : value;
		}

		// не число — вернуть строку
		return value;
	}

	if (Array.isArray(value)) return value.map(convertValue);

	if (value && typeof value === 'object') {
		const out: any = {};

		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		for (const [k, v] of Object.entries(value)) {
			out[k] = convertValue(v);
		}

		return out;
	}

	// number, boolean, null и т.п.
	return value;
}

export function parseJsonSmart(jsonText: string) {
	try {
		// быстрый проход
		const parsed = JSONbigFast.parse(jsonText);

		return convertValue(parsed);
	} catch {
		// fallback: парсим всё как строки, чтобы избежать ошибок BigInt при дробях
		const parsed = JSONbigSafe.parse(jsonText);

		return convertValue(parsed);
	}
}

export const objectToURLSearchParams = (parameters: unknown): string => {
	const searchParameters = new URLSearchParams();

	for (const key of Object.keys(parameters ?? {})) {
		const value = (<Record<string, string>>parameters)?.[key];

		if (value) {
			searchParameters.append(key, String(value).replaceAll('+', ','));
		}
	}

	return searchParameters.toString();
};

const addHeaderIfExists = (key: string, value: string | null | undefined) => {
	return value ? { [key]: value } : {};
};

// Преобразовывает конфиг, напоминающий axios к виду, который ожидает fetch
const getRequestConfig = <RequestBody = BodyInit>(method: string, config: IApiFetchConfig<RequestBody>) => {
	const { params, body, headers = {}, ...other } = config;
	const isFormData = body instanceof FormData;
	const isGet = method === 'GET';
	const contentType = headers['Content-Type'] ?? 'application/json';

	if ((isFormData && !headers['Content-Type']) || isGet) {
		delete headers['Content-Type'];
	} else {
		headers['Content-Type'] = contentType;
	}

	const requestConfig: IRequestConfig = { ...other, headers, method };

	if (body) {
		if (isFormData) {
			requestConfig.body = body;
		} else if (contentType === 'application/x-www-form-urlencoded') {
			requestConfig.body = objectToURLSearchParams(body);
		} else if (contentType === 'application/json') {
			requestConfig.body = JSONbigSafe.stringify(body);
		} else {
			throw new Error(`Unsupported Content-Type: ${contentType}`);
		}
	}

	if (params) {
		requestConfig.params = objectToURLSearchParams(params);
	}

	return requestConfig;
};

const getCookies = () => {
	// let i18NEXT_LOCALE;
	// let session_id;

	// const jsCookieModule = await import('js-cookie');
	// const jsCookie = jsCookieModule.default;

	//const i18NEXT_LOCALE = Cookies.get('i18next');
	const SESSION_TOKEN = Cookies.get(AUTH_COOKIE_NAME);

	return {
		Authorization: SESSION_TOKEN || '',
		//Language: i18NEXT_LOCALE || 'en',
	};
};

// Формирует и выполняет запрос
async function requestConfig<ReqRes>(api: string, route: string, config: IRequestConfig = {}): Promise<ReqRes> {
	try {
		const { campaignId } = useCampaignStore.getState();
		const { params, headers, cache = 'no-cache', ...init } = config;

		const url = `${api}/${route}${params ? `?${params}` : ''}`;

		const launchParameters = retrieveLaunchParams();

		const cookies = await getCookies();

		const baseHeaders: Record<string, string> = {
			'Content-Type': 'application/json; charset=utf-8',
			...cookies,
			...headers,
			...addHeaderIfExists('X-Campaign-Id', campaignId),
			...(campaignId ? addHeaderIfExists('X-Click-Id', launchParameters.tgWebAppStartParam) : {}),
		};

		if (launchParameters.tgWebAppStartParam !== undefined && !campaignId) {
			baseHeaders.Promocode = launchParameters.tgWebAppStartParam;
		}

		const initParameters: RequestInit = {
			cache,
			headers: baseHeaders,
			...init,
		};

		const res = await fetch(url, initParameters);

		if (!res.ok) {
			let body: any = null;
			let rawText = '';

			rawText = await res.text();

			try {
				body = parseJsonSmart(rawText);
			} catch {
				body = { message: rawText };
			}

			if (res.status < 500) {
				if (res.status === 401) {
					API_EVENT_EMITTER.emit('apiError', {
						response: { data: body },
						status: res.status,
					});
					// if (body?.enable_two_fa === undefined && body?.pin_code === undefined) {
					// 	API_EVENT_EMITTER.emit('logout');
					// }
				}

				throw { response: { data: body }, status: res.status };
			} else {
				const lang = Cookies.get('i18next');

				toast.error(String(body.message) || ERROR_MESSAGES[lang || 'en']);

				throw { response: { data: null }, status: res.status };
			}
		}

		if (res.status === 204) {
			return await Promise.resolve({} as ReqRes);
		}

		const text = await res.text();

		try {
			const parsed = parseJsonSmart(text);

			return (typeof parsed === 'object' && parsed !== null) || Array.isArray(parsed)
				? parsed
				: await Promise.resolve({} as ReqRes);
		} catch (error) {
			console.log(error);

			return await Promise.resolve({} as ReqRes);
		}
	} catch (error: unknown) {
		if ((error as IResponseErrors)?.status === 401) {
			API_EVENT_EMITTER.emit('apiError', { status: error as IResponseErrors });

			// if (
			// 	(error as IResponseErrors)?.response?.data?.enable_two_fa === undefined &&
			// 	(error as IResponseErrors)?.response?.data?.pin_code === undefined
			// )
			// 	API_EVENT_EMITTER.emit('logout');
		}

		// console.error('Error during request:', error);
		throw error; // Re-throw the error to propagate it further if needed
	}
}

interface ApiFetch<T = BodyInit> {
	delete<ReqRes, RequestBody = BodyInit>(
		url: string,
		config?: IApiFetchConfig<T & RequestBody>,
		allData?: boolean,
	): Promise<ReqRes>;
	get<ReqRes>(url: string, config?: IApiFetchConfig<T>, allData?: boolean): Promise<ReqRes>;
	post<ReqRes, RequestBody = BodyInit>(
		url: string,
		config?: IApiFetchConfig<T & RequestBody>,
		allData?: boolean,
	): Promise<ReqRes>;
	put<ReqRes, RequestBody = BodyInit>(
		url: string,
		config?: IApiFetchConfig<T & RequestBody>,
		allData?: boolean,
	): Promise<ReqRes>;
}

export function createApiFetch<T>(api: string): ApiFetch<T> {
	return {
		// eslint-disable-next-line max-len
		delete<ReqRes, RequestBody = BodyInit>(
			url: string,
			config: IApiFetchConfig<T & RequestBody> = {},
		): Promise<ReqRes> {
			return requestConfig(api, url, getRequestConfig('DELETE', config));
		},
		// eslint-disable-next-line max-len
		get<ReqRes>(url: string, config: IApiFetchConfig<T> = {}): Promise<ReqRes> {
			return requestConfig(api, url, getRequestConfig('GET', config));
		},
		// eslint-disable-next-line max-len
		post<ReqRes, RequestBody = BodyInit>(url: string, config: IApiFetchConfig<T & RequestBody> = {}): Promise<ReqRes> {
			return requestConfig(api, url, getRequestConfig('POST', config));
		},
		// eslint-disable-next-line max-len
		put<ReqRes, RequestBody = BodyInit>(url: string, config: IApiFetchConfig<T & RequestBody> = {}): Promise<ReqRes> {
			return requestConfig(api, url, getRequestConfig('PUT', config));
		},
	};
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const appApi = createApiFetch(process.env.BASE_URL_API!);
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const geoApi = createApiFetch(process.env.GEO_API_URL!);
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const ticketControlApi = createApiFetch(process.env.TICKET_CONTROL_API!);

export default appApi;
export { appApi, geoApi, ticketControlApi };
